import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Skeleton,
    TextField,
    Tabs,
    Tab,
    useTheme,
} from '@mui/material';
import { createUseStyles } from 'react-jss';
import { PageHeader, Panel } from '../../../components/ui';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import PageContainer from '../../../components/PageContainer';
import { Stories } from '../../../store/slices';
import { IStore } from '../../../store/types';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ChartDataLabels
);

const useStyles = createUseStyles((theme: any) => {
    const { color, mq } = theme.custom;
    return {
        kpis: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 20, marginBottom: 20, [mq.sm]: { gridTemplateColumns: '1fr', gap: 12, marginBottom: 16 } },
        kpi: { display: 'flex', flexDirection: 'column', gap: 6 },
        kpiLabel: { fontSize: 12, fontWeight: 500, color: color.text.secondary },
        kpiValue: { fontSize: 32, fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' },
        grid: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 20, marginBottom: 20, [mq.sm]: { gridTemplateColumns: '1fr', gap: 12, marginBottom: 16 } },
        rows: { display: 'flex', flexDirection: 'column' },
        row: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: '8px 0', borderBottom: `1px solid ${color.border}`, fontSize: 14, '&:last-child': { borderBottom: 'none' } },
        rowLabel: { color: color.text.secondary },
        rowValue: { fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
        subhead: { fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: color.text.secondary, margin: '12px 0 4px', '&:first-child': { marginTop: 0 } },
        chartTools: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
        chart: { height: 400, [mq.sm]: { height: 300 } },
    };
});

const StoriesStats = () => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const theme = useTheme();
    const { stats, graph, status } = useSelector((state: IStore) => ({
        stats: state.stories.stats,
        graph: state.stories.graph,
        status: state.stories.status
    }));

    const [graphType, setGraphType] = useState<'day' | 'month'>('day');
    const [days, setDays] = useState(90);
    const [tabIndex, setTabIndex] = useState(0);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabIndex(newValue);
    };

    useEffect(() => {
        dispatch<any>(Stories.getStoriesStats());
    }, [dispatch]);

    useEffect(() => {
        dispatch<any>(Stories.getStoriesGraph({ type: graphType, days }));
    }, [dispatch, graphType, days]);

    if (!stats && status === 'loading') {
        return (
            <PageContainer constrained>
                <PageHeader eyebrow="Customer Stories" title="Stories statistics" />
                <Skeleton animation="wave" variant="rounded" height={120} sx={{ mb: 2.5 }} />
                <Skeleton animation="wave" variant="rounded" height={320} />
            </PageContainer>
        );
    }

    if (!stats) return null;

    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const formatMonth = (monthName: string, year: string) => {
        return `${monthName} ${year}`;
    }

    const chartData = {
        labels: graph?.data?.map((item: any) => {
            if (graphType === 'month') {
                return item.monthName && item.year ? formatMonth(item.monthName, item.year) : item.month || '';
            }
            return formatDate(item.date);
        }) || [],
        datasets: [
            {
                label: 'Stories Created',
                data: graph?.data?.map((item: any) => item.count) || [],
                backgroundColor: theme.custom.color.primary.main,
                borderColor: theme.custom.color.primary.main,
                borderWidth: 0,
                borderRadius: 4,
                hoverBackgroundColor: theme.custom.color.primary.hover,
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false, // Hide legend as the title is sufficient
            },
            title: {
                display: false, // Hide internal title
            },
            datalabels: {
                anchor: 'end' as const,
                align: 'top' as const,
                color: theme.custom.color.text.secondary,
                font: {
                    weight: 'bold' as const,
                },
                formatter: (value: any) => value > 0 ? value : '', // Hide zeros if preferred
            },
            tooltip: {
                backgroundColor: theme.custom.color.tooltip.bg,
                padding: 12,
                titleFont: { size: 14 },
                bodyFont: { size: 14 },
                displayColors: false,
            }
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    maxRotation: 45,
                    minRotation: 0,
                    autoSkip: true,
                    maxTicksLimit: 20, // Limit ticks to avoid clutter
                    color: theme.custom.color.text.secondary,
                }
            },
            y: {
                beginAtZero: true,
                grace: '10%', // Add space on top for labels
                grid: {
                    color: theme.custom.color.border,
                },
                ticks: {
                    stepSize: 1,
                    precision: 0,
                    color: theme.custom.color.text.secondary,
                }
            }
        },
        layout: {
            padding: {
                top: 20,
            }
        }
    };

    const kpi = (label: string, value: React.ReactNode, color?: string) => (
        <Panel>
            <div className={classes.kpi}>
                <span className={classes.kpiLabel}>{label}</span>
                <span className={classes.kpiValue} style={color ? { color } : undefined}>{value}</span>
            </div>
        </Panel>
    );
    const row = (label: string, value: React.ReactNode, key?: string | number) => (
        <div className={classes.row} key={key ?? label}>
            <span className={classes.rowLabel}>{label}</span>
            <span className={classes.rowValue}>{value}</span>
        </div>
    );

    return (
        <PageContainer constrained>
            <PageHeader eyebrow="Customer Stories" title="Stories statistics">
                <Tabs value={tabIndex} onChange={handleTabChange} aria-label="Statistics and growth graph">
                    <Tab label="Statistics" />
                    <Tab label="Growth graph" />
                </Tabs>
            </PageHeader>

            {tabIndex === 0 && (
                <>
                    <div className={classes.kpis}>
                        {kpi('Total stories', stats.overview.total)}
                        {kpi('Published', stats.overview.published, theme.custom.color.tone.success.main)}
                        {kpi('Unpublished', stats.overview.unpublished, theme.custom.color.tone.warning.main)}
                    </div>
                    <div className={classes.grid}>
                        <Panel title="Timeline activity">
                            <div className={classes.rows}>
                                {row('Last 24 hours', stats.timeline.last24Hours)}
                                {row('Last 7 days', stats.timeline.last7Days)}
                                {row('Last 30 days', stats.timeline.last30Days)}
                                {row('Average per day', stats.timeline.averagePerDay)}
                                {row('Average per month', stats.timeline.averagePerMonth)}
                            </div>
                        </Panel>
                        <Panel title="Media">
                            <div className={classes.subhead}>Videos</div>
                            <div className={classes.rows}>
                                {row('Primary video', stats.media.videos.withPrimaryVideo)}
                                {row('Secondary video', stats.media.videos.withSecondaryVideo)}
                            </div>
                            <div className={classes.subhead}>Images</div>
                            <div className={classes.rows}>
                                {row('Stories with images', `${stats.media.storyImages.storiesWithImages} (${stats.media.storyImages.storiesWithImagesPercentage}%)`)}
                                {row('Average images per story', stats.media.storyImages.averageImagesPerStory)}
                            </div>
                        </Panel>
                    </div>
                    <div className={classes.grid}>
                        <Panel title="Top occasions">
                            <div className={classes.rows}>
                                {stats.topOccasions.map((occasion: any) => row(occasion.name, occasion.count, occasion.id))}
                            </div>
                        </Panel>
                        <Panel title="Top recipients">
                            <div className={classes.rows}>
                                {stats.topRecipients.map((recipient: any) => row(recipient.name, recipient.count, recipient.id))}
                            </div>
                        </Panel>
                    </div>
                </>
            )}

            {tabIndex === 1 && (
                <Panel
                    title="Stories created"
                    actions={(
                        <div className={classes.chartTools}>
                            <FormControl size="small" sx={{ minWidth: 130 }}>
                                <InputLabel id="stories-graph-type">Granularity</InputLabel>
                                <Select labelId="stories-graph-type" value={graphType} label="Granularity" onChange={(e) => setGraphType(e.target.value as 'day' | 'month')}>
                                    <MenuItem value="day">Daily</MenuItem>
                                    <MenuItem value="month">Monthly</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                type="number"
                                size="small"
                                label="Days"
                                value={days}
                                inputProps={{ min: 1, inputMode: 'numeric' }}
                                onChange={(e) => setDays(Number(e.target.value))}
                                sx={{ width: 110 }}
                            />
                        </div>
                    )}
                >
                    <Box className={classes.chart}>
                        <Bar options={options} data={chartData} />
                    </Box>
                </Panel>
            )}
        </PageContainer>
    );
};

export default StoriesStats;
